'use client';

/**
 * ReusableDocumentPicker Component
 *
 * Consumer-facing simplified picker for reusing previously uploaded documents.
 * Shows a list of document cards with "Use for This Loan" action buttons.
 */

import CheckCircle from '@mui/icons-material/CheckCircle';
import Description from '@mui/icons-material/Description';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';
import { GlassCard } from '../GlassCard/GlassCard';
import { GlassChip } from '../GlassChip/GlassChip';
import { type DocumentScope, DocumentScopeBadge } from './DocumentScopeBadge';

// ============================================================================
// Types
// ============================================================================

export interface ReusableDocument {
  /** Document identifier */
  documentId: string;
  /** Document display name */
  name: string;
  /** Document type label */
  documentType: string;
  /** Document scope */
  scope: DocumentScope;
  /** Whether this document is already linked to the current loan */
  alreadyLinked: boolean;
  /** When the document was previously uploaded */
  uploadedAt?: string;
}

export interface ReusableDocumentPickerProps {
  /** List of reusable documents */
  documents: ReusableDocument[];
  /** Callback when a document is reused for the current loan */
  onReuse: (documentId: string) => void;
  /** Whether the picker is in a loading state */
  isLoading?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ReusableDocumentPicker = ({
  documents,
  onReuse,
  isLoading = false,
}: ReusableDocumentPickerProps): React.ReactElement => {
  const { t, formatRelativeTime } = useI18n();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (documents.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          textAlign: 'center',
        }}
      >
        <Box sx={{ mb: 1, color: 'text.disabled', '& > svg': { fontSize: iconSizes.xl } }}>
          <Description />
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('dataRoom.empty.title')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('dataRoom.reuse.title')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.disabled',
          mb: 1,
        }}
      >
        {t('dataRoom.reuse.description')}
      </Typography>
      {documents.map((doc) => (
        <GlassCard key={doc.documentId} variant="outlined">
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            {/* Document info */}
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {doc.name}
                </Typography>
                <DocumentScopeBadge scope={doc.scope} />
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {doc.documentType}
                </Typography>
                {doc.uploadedAt && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                    }}
                  >
                    {t('dataRoom.reuse.previouslyUploaded')} {formatRelativeTime(doc.uploadedAt)}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* Action */}
            {doc.alreadyLinked ? (
              <GlassChip
                icon={<CheckCircle sx={{ fontSize: iconSizes.sm }} />}
                label={t('dataRoom.reuse.alreadyLinked')}
                color="success"
                size="small"
                variant="outlined"
                glowOnHover={false}
              />
            ) : (
              <GlassButton variant="gradient" size="small" onClick={() => onReuse(doc.documentId)}>
                {t('dataRoom.reuse.useForLoan')}
              </GlassButton>
            )}
          </Stack>
        </GlassCard>
      ))}
    </Stack>
  );
};

ReusableDocumentPicker.displayName = 'ReusableDocumentPicker';

export default ReusableDocumentPicker;
